import { Component, computed, effect, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DynamicForm } from '../../../components/form-builder/form-builder.types';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { RefParamsService } from './ref-params.service';
import { RefParams, RefType } from '@/types';
import { getRefParamsDataTableStructure } from './utils/ref-params-data-table-object';
import { getRefParamsFormObject } from './utils/ref-params-form-object';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { RefTypeService } from '../ref-type/ref-type.service';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { getRefParamsUpdateDrawerObject } from './utils/ref-params-update-drawer-object';
import { RefParamRepository } from '../../../store/ref-param-state/ref-param-state.repository';
import { MessageService } from 'primeng/api';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { getRefParamsAddDrawerObject } from './utils/ref-params-add-drawer-object';
import { getRefParamsDeleteObject } from './utils/ref-params-delete-dialog-object';
import { RefParamsDto } from '@/types/dtos.types';
import { refParamsSchema } from '../../../validations/ref-param.validation';
import { TranslateService } from '@ngx-translate/core';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-ref-params',
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    PageHeaderComponent,
    DrawerComponent,
    DataTableBuilderComponent,
    DialogComponent,
  ],
  templateUrl: './ref-params.component.html',
  styleUrl: './ref-params.component.scss',
})
export class RefParamsComponent implements OnInit {
  //services
  refParamsService = inject(RefParamsService);
  refTypeService = inject(RefTypeService);
  refParamRepository = inject(RefParamRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.refParams.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.refParams.breadcrumb.refParams',
      routerLink: ['/settings', 'ref-params'],
    },
  ];

  //drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  refParamsDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  refParamsDialog?: DialogObject;

  //form-object
  refTypes: RefType[] = [];
  refTypesOptions: { label: string; value: number }[] = [];
  formObject: DynamicForm = getRefParamsFormObject({ store: this.refParamRepository, refTypes: this.refTypes });

  //data-table
  //server-query
  serverQuery = createServerQuery({});
  queryParams = computed(() => ({
    page: this.serverQuery.page(),
    pageSize: this.serverQuery.pageSize(),
    sortBy: this.serverQuery.sortBy(),
    sortOrder: this.serverQuery.sortOrder(),
    search: this.serverQuery.search(),
  }));

  //row-actions
  rowActions: DataTableRowActions = {
    editAction: () => {
      this.formObject = getRefParamsFormObject({ store: this.refParamRepository, refTypes: this.refTypes });
      this.refParamsDrawer = getRefParamsUpdateDrawerObject({
        store: this.refParamRepository,
        formObject: this.formObject,
        action: () => this.editRefType(this.refParamRepository.getNested('response', 'id'), this.refParamRepository.getNested('dto')),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: () => {
      this.refParamsDialog = getRefParamsDeleteObject({
        t: this.translate,
        store: this.refParamRepository,
        representation: this.refParamRepository.getNested('response', 'code'),
        action: () => {
          this.deleteRefParams(this.refParamRepository.getNested('response', 'id'));
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  loading = true;
  data: RefParams[] = [];
  totalRecords = 0;
  dataTableObject: DynamicDataTable = getRefParamsDataTableStructure({
    store: this.refParamRepository,
    createAction: () => {
      this.refParamsDrawer = getRefParamsAddDrawerObject({
        store: this.refParamRepository,
        formObject: this.formObject,
        action: () => this.addRefParams(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    rowActions: this.rowActions,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.loadData();
    this.refTypeService.getRefTypes().subscribe(data => {
      this.refTypes = data;
      this.refTypesOptions = this.refTypes.map(refType => {
        return { label: refType.description, value: refType.id };
      });
      // Update formObject based on new refTypes
      this.formObject = getRefParamsFormObject({ store: this.refParamRepository, refTypes: this.refTypes });
      // Recreate the drawer object with the updated formObject
      this.refParamsDrawer = getRefParamsUpdateDrawerObject({
        formObject: this.formObject,
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
        action: () => this.addRefParams(),
      });
    });
  }

  loadData() {
    this.refParamsService
      .getRefParams({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = data.totalElements;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.refParams.message.error.loading'),
          });
        },
      });
  }

  addRefParams() {
    const result = refParamsSchema.safeParse(this.refParamRepository.get('dto'));
    if (result.success) {
      this.refParamsService.addRefParams(this.refParamRepository.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.refParamRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.refParams.message.success.added'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.refParams.message.error.added'),
          });
        },
      });
    } else {
      this.refParamRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  editRefType(id: number, refParams: RefParamsDto) {
    const result = refParamsSchema.safeParse(this.refParamRepository.get('dto'));
    if (result.success) {
      this.refParamsService.updateRefParams(id, refParams).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.refParamRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.refParams.message.success.edited'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.refParams.message.error.edited'),
          });
        },
      });
    } else {
      this.refParamRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  deleteRefParams(id: number) {
    this.refParamsService.deleteRefParams(id).subscribe({
      next: () => {
        this.loadData();
        this.refParamRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.refParams.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.refParams.message.error.deleted'),
        });
      },
    });
  }
}
